import { ItemTagPicker } from '../sharing/ItemTagPicker';
import { useEditorSession } from '../../hooks/useEditorSession';
import {
  useNavigation,
  useNavigationState,
} from '../navigation/NavigationProvider';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  InventoryAuthority,
  CollectionSummary,
  hasCollectionCapability,
} from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { logger } from '../../services/logger';
import { useFeedback } from '../feedback/FeedbackProvider';
import { useItemImageWorkflow } from '../images/useItemImageWorkflow';
import { initialItemEditorState, itemEditorReducer } from './itemEditorReducer';
import { useItemActions } from './useItemActions';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useLocationEditorActions } from '../locations/useLocationEditorActions';
import { collectionCapabilities } from '../collection/collectionCapabilities';
import { recordKey } from '../collection/multiCollectionIdentity';
import { RapidCaptureDraft } from '../rapidCapture/rapidCaptureTypes';

interface AddItemBaseline {
  keywords: string;
  location: string;
  qty: number;
  hasImage: boolean;
}

// URLs can refresh independently of persisted content.
const itemRevision = (item: Item) =>
  JSON.stringify([
    item.keywords,
    item.location,
    item.locationId,
    item.qty,
    item.storagePath,
    item.revision,
    item.tagIds,
  ]);
const numericQuantity = (qty: number | string) =>
  typeof qty === 'string' ? Number.parseFloat(qty) || 0 : qty;

export function useItemWorkflows({
  currentUsername,
  items,
  locations,
  lastUsedLocation,
  updateLastUsedLocation,
  onAddLocation,
  onCapacityChanged,
  collectionContext,
  billingRestricted,
  moveLocations = [],
  onMoveItem,
  moveLocationsNotice,
  moveLocationsLoading = false,
  enqueuePhoto,
  addCollectionId,
  showTagCollectionName = false,
  onManageTags,
}: {
  showTagCollectionName?: boolean;
  onManageTags?: (collectionId: string) => void;
  collectionContext: { userId: string; collections: CollectionSummary[] };
  billingRestricted?: Set<string>;
  moveLocations?: Location[];
  moveLocationsNotice?: string;
  moveLocationsLoading?: boolean;
  onMoveItem?: (
    item: Item,
    destination: Location,
    destinationTagIds?: string[],
  ) => Promise<boolean>;
  currentUsername?: string;
  items: Item[];
  locations: Location[];
  lastUsedLocation: string;
  updateLastUsedLocation: (location: string) => Promise<void>;
  onAddLocation: (collectionId?: string) => void;
  onCapacityChanged?: () => void;
  enqueuePhoto?: (draft: RapidCaptureDraft) => Promise<unknown>;
  addCollectionId?: string;
}) {
  const { notify } = useFeedback();
  const { updateStates, requestTransition } = useNavigation();
  const [editorRoute, setEditorRoute] = useNavigationState<{
    mode: 'add' | 'details';
    collectionId?: string;
    itemId?: string;
  } | null>('inventory.editor', null);
  const editorMode = editorRoute?.mode;
  const setEditorMode = (mode: 'add' | 'details' | null) =>
    setEditorRoute(
      mode
        ? {
            mode,
            ...(mode === 'details'
              ? {
                  collectionId: selectedItemSnapshot?.collectionId,
                  itemId: selectedItemSnapshot?.id,
                }
              : {}),
          }
        : null,
    );
  const addOpen = editorMode === 'add';
  const detailsOpen = editorMode === 'details';
  const [deleteItemId, setDeleteItemId] = useNavigationState<string | null>(
    'inventory.delete',
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addUiPending, setAddUiPending] = useState(false);
  const [tagsPending, setTagsPending] = useState(false);
  const savePending = useRef(false);
  const runSave = async (action: () => Promise<boolean>) => {
    if (savePending.current) return false;
    savePending.current = true;
    setSaving(true);
    try {
      return await action();
    } finally {
      savePending.current = false;
      setSaving(false);
    }
  };
  const deleteOpen = deleteItemId !== null;
  const [lastSelectedItem, setLastSelectedItem] = useState<Item | null>(null);
  const itemSnapshots = useRef(new Map<string, Item>());
  const deletedItems = useRef(new Set<string>());
  const savedItems = useRef(
    new Map<string, { sourceRevision: string; item: Item }>(),
  );
  const setSelectedItem = useCallback((item: Item | null) => {
    if (item) {
      const key = recordKey(item);
      const saved = savedItems.current.get(key);
      itemSnapshots.current.set(
        key,
        saved?.sourceRevision === itemRevision(item) ? saved.item : item,
      );
    }
    setLastSelectedItem(item);
  }, []);
  // Reconcile the restoration cache without changing an active editor baseline.
  const observedItems = useRef(new Map<string, Item>());
  items.forEach((item) => {
    const key = recordKey(item);
    if (observedItems.current.get(key) !== item) {
      observedItems.current.set(key, item);
      const saved = savedItems.current.get(key);
      if (saved && saved.sourceRevision !== itemRevision(item))
        savedItems.current.delete(key);
      itemSnapshots.current.set(key, savedItems.current.get(key)?.item || item);
    }
  });
  const detailKey =
    editorRoute?.mode === 'details'
      ? recordKey({
          id: editorRoute.itemId || '',
          collectionId: editorRoute.collectionId,
        })
      : null;
  const detailAvailable =
    detailKey !== null &&
    !deletedItems.current.has(detailKey) &&
    collectionContext.collections.some(
      (collection) =>
        collection.collectionId === editorRoute?.collectionId &&
        collection.lifecycle !== 'deleted',
    );
  const detailBaseline = useEditorSession(
    detailAvailable ? detailKey : null,
    detailAvailable ? itemSnapshots.current.get(detailKey!) || null : null,
  );
  const selectedItemSnapshot = detailsOpen ? detailBaseline : lastSelectedItem;
  const currentSelectedItem =
    selectedItemSnapshot &&
    items.find(
      (item) =>
        item.collectionId === selectedItemSnapshot.collectionId &&
        item.id === selectedItemSnapshot.id &&
        item.storagePath === selectedItemSnapshot.storagePath,
    );
  // Image URLs resolve independently of the editor. Keep its original field
  // baseline while sharing the current URL with display, clear and duplicate.
  const selectedItem =
    selectedItemSnapshot && currentSelectedItem
      ? { ...selectedItemSnapshot, image: currentSelectedItem.image }
      : selectedItemSnapshot;
  const requestSelectedImage = currentSelectedItem?.requestImage;
  useEffect(() => {
    if (detailsOpen) return requestSelectedImage?.(0);
  }, [detailsOpen, requestSelectedImage]);
  const [duplicateSourceItemId, setDuplicateSourceItemId] = useState<string>();
  const [duplicateDraft, setDuplicateDraft] = useState(false);
  const [addBaseline, setAddBaseline] = useState<AddItemBaseline | null>(null);
  const [addRapidCapture, setAddRapidCapture] = useState(false);
  const [activeLastUsedLocation, setActiveLastUsedLocation] =
    useState(lastUsedLocation);
  useEffect(() => {
    setActiveLastUsedLocation(lastUsedLocation);
  }, [lastUsedLocation]);
  const applyLastUsedLocation = useCallback(
    async (locationToSave: string) => {
      if (!locationToSave) return;
      setActiveLastUsedLocation(locationToSave);
      await updateLastUsedLocation(locationToSave);
    },
    [updateLastUsedLocation],
  );
  const isItemWritable = useCallback(
    (candidateItem: Item): boolean => {
      const coll = candidateItem.collectionId
        ? collectionContext.collections.find(
            (c) => c.collectionId === candidateItem.collectionId,
          )
        : collectionContext.collections[0];
      if (!coll) return false;
      if (billingRestricted?.has(coll.collectionId)) return false;
      return collectionCapabilities(
        'collection-only',
        coll.role,
        coll.lifecycle,
      ).canEditInventory;
    },
    [collectionContext.collections, billingRestricted],
  );
  const [editorState, dispatchEditor] = useReducer(
    itemEditorReducer,
    initialItemEditorState,
  );
  const setTagIds = useCallback(
    (value: string[]) => dispatchEditor({ type: 'setTags', value }),
    [],
  );
  const setKeywords = useCallback(
    (value: string) => dispatchEditor({ type: 'setKeywords', value }),
    [],
  );
  const allLocations = useMemo(
    () => [
      ...new Map(
        [...locations, ...moveLocations].map((entry) => [
          recordKey(entry),
          entry,
        ]),
      ).values(),
    ],
    [locations, moveLocations],
  );
  const findItemLocation = useCallback(
    (item: Item) =>
      allLocations.find(
        (entry) =>
          (item.collectionId
            ? entry.collectionId === item.collectionId
            : true) &&
          (item.locationId
            ? entry.id === item.locationId
            : entry.name === item.location),
      ) ||
      locations.find(
        (entry) =>
          (item.collectionId
            ? entry.collectionId === item.collectionId
            : true) &&
          (item.locationId
            ? entry.id === item.locationId
            : entry.name === item.location),
      ),
    [allLocations, locations],
  );
  const selectedLocation = selectedItem && findItemLocation(selectedItem);
  const pendingItemLocation = useRef<Item | null>(null);
  const setLocation = useCallback((value: string) => {
    pendingItemLocation.current = null;
    dispatchEditor({ type: 'setLocation', value });
  }, []);
  useEffect(() => {
    const pending = pendingItemLocation.current;
    if (!detailsOpen || !pending) return;
    const resolved = findItemLocation(pending);
    if (resolved) {
      const key = recordKey(resolved);
      setLocation(key);
      if (isItemWritable(pending)) {
        void applyLastUsedLocation(key);
      }
    }
  }, [
    detailsOpen,
    findItemLocation,
    setLocation,
    isItemWritable,
    applyLastUsedLocation,
  ]);
  const setQty = useCallback(
    (value: number | string) => dispatchEditor({ type: 'setQty', value }),
    [],
  );

  const chosenLocation = allLocations.find(
    (candidate) => recordKey(candidate) === editorState.location,
  );
  const targetCollectionId = detailsOpen
    ? selectedItem?.collectionId
    : chosenLocation?.collectionId;
  const targetCollection = collectionContext.collections.find(
    (candidate) => candidate.collectionId === targetCollectionId,
  );
  const targetCanDelete =
    targetCollection &&
    collectionCapabilities(
      'collection-only',
      targetCollection.role,
      targetCollection.lifecycle,
    ).canEditInventory;
  const targetCanEdit =
    targetCanDelete && !billingRestricted?.has(targetCollectionId || '');
  const targetAuthority: InventoryAuthority | undefined =
    targetCollection &&
    targetCanEdit &&
    (detailsOpen || targetCollection.lifecycle === 'active')
      ? {
          userId: collectionContext.userId,
          collectionId: targetCollection.collectionId,
          compatibilityMode: 'collection-only',
        }
      : undefined;
  const targetMembershipVersion = targetCollection?.membershipVersion;
  // AI needs collection authority, but saving still requires a chosen location.
  const addAiCollection = chosenLocation
    ? targetCollection
    : collectionContext.collections.find(
        (collection) => collection.collectionId === addCollectionId,
      );
  const addAiAuthority =
    addAiCollection?.lifecycle === 'active' &&
    !billingRestricted?.has(addAiCollection.collectionId) &&
    collectionCapabilities(
      'collection-only',
      addAiCollection.role,
      addAiCollection.lifecycle,
    ).canEditInventory
      ? {
          userId: collectionContext.userId,
          collectionId: addAiCollection.collectionId,
          compatibilityMode: 'collection-only' as const,
          membershipVersion: addAiCollection.membershipVersion,
        }
      : undefined;
  const scopedLocations = locations.filter(
    (candidate) => candidate.collectionId === targetCollectionId,
  );
  const canMove = Boolean(
    onMoveItem &&
    targetCanEdit &&
    targetCollection?.lifecycle === 'active' &&
    hasCollectionCapability(targetCollection.role, 'moveItemsOut'),
  );
  const destinationSummary = collectionContext.collections.find(
    (entry) => entry.collectionId === chosenLocation?.collectionId,
  );
  const crossCollectionLocation =
    detailsOpen &&
    chosenLocation &&
    chosenLocation.collectionId !== selectedItem?.collectionId &&
    canMove &&
    destinationSummary?.lifecycle === 'active' &&
    hasCollectionCapability(destinationSummary.role, 'editInventory')
      ? chosenLocation
      : undefined;
  const destinationLocations = (
    detailsOpen && canMove ? allLocations : locations
  ).filter((candidate) => {
    const summary = collectionContext.collections.find(
      (entry) => entry.collectionId === candidate.collectionId,
    );
    return (
      summary &&
      (detailsOpen
        ? candidate.collectionId === selectedItem?.collectionId ||
          (canMove &&
            summary.lifecycle === 'active' &&
            hasCollectionCapability(summary.role, 'editInventory'))
        : summary.lifecycle === 'active' &&
          summary.role !== 'viewer' &&
          (!duplicateSourceItemId ||
            candidate.collectionId === selectedItem?.collectionId))
    );
  });
  const tagCollection = crossCollectionLocation
    ? destinationSummary
    : targetCollection || addAiCollection;
  const canManageTags = Boolean(
    tagCollection &&
    ['owner', 'administrator'].includes(tagCollection.role) &&
    tagCollection.lifecycle === 'active' &&
    !billingRestricted?.has(tagCollection.collectionId),
  );
  const tagScope = tagCollection?.collectionId;
  const previousTagScope = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      previousTagScope.current &&
      tagScope &&
      previousTagScope.current !== tagScope
    ) {
      setTagIds([]);
      if (editorState.tagIds.length)
        notify(
          'Tags were cleared because the destination collection changed.',
          'info',
        );
    }
    previousTagScope.current = tagScope;
  }, [tagScope, editorState.tagIds.length, setTagIds, notify]);
  const tagsChanged =
    JSON.stringify([...editorState.tagIds].sort()) !==
    JSON.stringify([...(selectedItem?.tagIds || [])].sort());
  const tagControl =
    addOpen || detailsOpen
      ? createElement(ItemTagPicker, {
          key: `${collectionContext.userId}:${tagScope || ''}`,
          collectionId: tagScope,
          collectionName: tagCollection?.name,
          showCollectionName: showTagCollectionName,
          value: editorState.tagIds,
          canManage: canManageTags,
          disabled: saving || addUiPending,
          onChange: setTagIds,
          onManageTags,
          onPendingChange: setTagsPending,
        })
      : undefined;
  const scopedItems = items.filter(
    (candidate) => candidate.collectionId === targetCollectionId,
  );
  if (
    selectedItem &&
    selectedItem.collectionId === targetCollectionId &&
    !scopedItems.some((entry) => entry.id === selectedItem.id)
  )
    scopedItems.push(selectedItem);
  const editorActions = useLocationEditorActions(targetAuthority);

  const imageWorkflow = useItemImageWorkflow({
    selectedItem,
    addOpen,
    detailsOpen,
    lastUsedLocation: activeLastUsedLocation,
    setLocation,
    setQty,
    openAdd: () => {
      if (!addOpen) setTagIds([]);
      setAddBaseline({
        keywords: '',
        location: activeLastUsedLocation,
        qty: 1,
        hasImage: false,
      });
      setEditorMode('add');
    },
  });

  // History entries carry identity only. Returning to another item must never
  // show the previous item's local fields or pending image against that identity.
  const restoredEditor = useRef<string | null>(null);
  useEffect(() => {
    if (editorRoute?.mode === 'details' && !detailAvailable) {
      setSelectedItem(null);
      if (detailKey) itemSnapshots.current.delete(detailKey);
      updateStates(
        { 'inventory.editor': null, 'inventory.delete': null },
        { replace: true },
      );
    }
  }, [editorRoute, detailAvailable, detailKey, setSelectedItem, updateStates]);
  const editorIdentity =
    editorRoute?.mode === 'details'
      ? JSON.stringify([editorRoute.collectionId, editorRoute.itemId])
      : editorRoute?.mode || null;
  useEffect(() => {
    if (restoredEditor.current === editorIdentity) return;
    if (detailsOpen && !selectedItemSnapshot) return;
    restoredEditor.current = editorIdentity;
    if (!detailsOpen || !selectedItemSnapshot) return;
    setKeywords(selectedItemSnapshot.keywords);
    const location = findItemLocation(selectedItemSnapshot);
    setLocation(location ? recordKey(location) : '');
    pendingItemLocation.current = location ? null : selectedItemSnapshot;
    setQty(selectedItemSnapshot.qty || 1);
    setTagIds(selectedItemSnapshot.tagIds || []);
    imageWorkflow.reset();
  }, [
    editorIdentity,
    detailsOpen,
    selectedItemSnapshot,
    findItemLocation,
    setKeywords,
    setLocation,
    setQty,
    setTagIds,
    imageWorkflow,
  ]);

  const resetItemForm = (nextLocation = '') => {
    setDuplicateDraft(false);
    setDuplicateSourceItemId(undefined);
    setKeywords('');
    setLocation(nextLocation);
    setQty(1);
    setTagIds([]);
    imageWorkflow.reset();
  };

  const actions = useItemActions({
    authority: targetAuthority,
    deletionAuthority:
      targetCollection && targetCanDelete
        ? {
            userId: collectionContext.userId,
            collectionId: targetCollection.collectionId,
            compatibilityMode: 'collection-only',
          }
        : undefined,
    items: scopedItems,
    locations: scopedLocations,
    keywords: editorState.keywords,
    location:
      chosenLocation?.collectionId === targetCollectionId
        ? chosenLocation?.name || ''
        : crossCollectionLocation
          ? selectedItem?.location || ''
          : '',
    qty: editorState.qty,
    image: imageWorkflow.image,
    imageBlob: imageWorkflow.croppedImageBlob,
    removeExistingImage: imageWorkflow.removeExistingImage,
    sourceItemId: addOpen ? duplicateSourceItemId : undefined,
    tagIds:
      canManageTags &&
      !crossCollectionLocation &&
      (addOpen ? editorState.tagIds.length > 0 : tagsChanged)
        ? editorState.tagIds
        : undefined,
    expectedRevision: selectedItem?.revision ?? (tagsChanged ? 0 : undefined),
    resetForm: (nextLocation) =>
      resetItemForm(nextLocation ? editorState.location : nextLocation),
    setLastUsedLocation: () => applyLastUsedLocation(editorState.location),
    setSelectedItem,
    setItems: () => undefined,
    closeAdd: () => closeAddAfterSave(),
    closeDetails: () => closeDetailsAfterSave(),
    onCapacityChanged,
    onUpdated: (fields) => {
      if (selectedItem) {
        const key = recordKey(selectedItem);
        const item = { ...selectedItem, ...fields };
        savedItems.current.set(key, {
          sourceRevision: itemRevision(
            items.find((candidate) => recordKey(candidate) === key) ||
              selectedItem,
          ),
          item,
        });
        itemSnapshots.current.set(key, item);
      }
    },
  });

  const saveAdd = (addAnother: boolean, analyzeImage = false) =>
    runSave(async () => {
      const photo = imageWorkflow.croppedImageBlob;
      if (tagsPending) return false;
      if (!photo || duplicateDraft || !enqueuePhoto) {
        return addAnother
          ? actions.saveAndAddAnother()
          : actions.saveAndClose();
      }
      if (
        !targetAuthority ||
        !chosenLocation ||
        imageWorkflow.compressionInProgress
      )
        return false;
      const destination = editorState.location;
      try {
        await enqueuePhoto({
          image: photo,
          keywords: editorState.keywords,
          location: chosenLocation.name,
          locationId: recordKey(chosenLocation),
          qty: numericQuantity(editorState.qty),
          analyzeImage,
          ...(canManageTags && editorState.tagIds.length
            ? { tagIds: editorState.tagIds }
            : {}),
        });
      } catch (error) {
        logger.error('Item enqueue failed.', error);
        notify(
          'The item could not be queued. Your draft is still open. Please try again.',
          'error',
        );
        return false;
      }
      resetItemForm(addAnother ? destination : '');
      if (addAnother)
        setAddBaseline({
          keywords: '',
          location: destination,
          qty: 1,
          hasImage: false,
        });
      else {
        setAddBaseline(null);
        closeAddAfterSave();
      }
      // This preference must not turn an accepted queue entry into a failed save.
      void applyLastUsedLocation(destination).catch(() => undefined);
      return true;
    });

  const addDirty =
    !addRapidCapture &&
    Boolean(addBaseline) &&
    (editorState.tagIds.length > 0 ||
      editorState.keywords !== addBaseline?.keywords ||
      editorState.location !== addBaseline?.location ||
      numericQuantity(editorState.qty) !== addBaseline?.qty ||
      Boolean(imageWorkflow.hasPendingImage || duplicateSourceItemId) !==
        addBaseline?.hasImage);
  const detailsDirty =
    Boolean(selectedItem) &&
    (tagsChanged ||
      editorState.keywords !== selectedItem?.keywords ||
      editorState.location !==
        (selectedLocation ? recordKey(selectedLocation) : '') ||
      numericQuantity(editorState.qty) !== (selectedItem?.qty || 1) ||
      Boolean(imageWorkflow.croppedImageBlob) ||
      imageWorkflow.removeExistingImage);
  const { requestClose: requestAddClose, closeAfterSave: closeAddAfterSave } =
    useUnsavedChangesGuard({
      scopeKey: 'inventory.editor',
      open: addOpen,
      dirty: addDirty,
      pending:
        imageWorkflow.compressionInProgress ||
        saving ||
        addUiPending ||
        tagsPending ||
        deleting,
      onDiscard: () => {
        resetItemForm();
        setAddBaseline(null);
      },
      onClose: () => setEditorMode(null),
    });
  const {
    requestClose: requestDetailsClose,
    closeAfterSave: closeDetailsAfterSave,
    runAfterSave: runDetailsAfterSave,
  } = useUnsavedChangesGuard({
    scopeKey: 'inventory.editor',
    open: detailsOpen,
    dirty: detailsDirty,
    pending:
      imageWorkflow.compressionInProgress ||
      saving ||
      addUiPending ||
      tagsPending ||
      deleting,
    onDiscard: () => {
      resetItemForm();
    },
    onClose: () => setEditorMode(null),
  });

  const resetImage = imageWorkflow.reset;
  const prepareAdd = useCallback(
    (location = activeLastUsedLocation) => {
      setDuplicateDraft(false);
      setDuplicateSourceItemId(undefined);
      setSelectedItem(null);
      resetImage();
      setKeywords('');
      setLocation(location);
      setQty(1);
      setTagIds([]);
      setAddBaseline({ keywords: '', location, qty: 1, hasImage: false });
    },
    [
      activeLastUsedLocation,
      resetImage,
      setKeywords,
      setLocation,
      setQty,
      setTagIds,
      setSelectedItem,
    ],
  );
  useEffect(() => {
    if (!addOpen) {
      setAddBaseline(null);
      return;
    }
    // Forward may restore an identity after its private draft has ended.
    if (addBaseline) return;
    prepareAdd();
  }, [addOpen, addBaseline, prepareAdd]);
  const openAdd = () => {
    void requestTransition({ 'inventory.editor': { mode: 'add' } }, () =>
      prepareAdd(),
    );
  };
  const openAddAtLocation = (location: Location, closeKey: string) =>
    requestTransition(
      { [closeKey]: null, 'inventory.editor': { mode: 'add' } },
      () => prepareAdd(recordKey(location)),
      { replace: true },
    );

  const openItem = (item: Item) => {
    void requestTransition(
      {
        'inventory.editor': {
          mode: 'details',
          collectionId: item.collectionId,
          itemId: item.id,
        },
      },
      () => {
        setDuplicateSourceItemId(undefined);
        imageWorkflow.reset();
        setSelectedItem(item);
        setKeywords(item.keywords);
        const resolved = findItemLocation(item);
        setLocation(resolved ? recordKey(resolved) : '');
        pendingItemLocation.current = resolved ? null : item;
        if (resolved && isItemWritable(item))
          void applyLastUsedLocation(recordKey(resolved));
        setQty(item.qty || 1);
        setTagIds(item.tagIds || []);
      },
    );
  };

  const requestDelete = (itemId: string) => {
    setDeleteItemId(itemId);
  };

  const cancelDelete = () => {
    setDeleteItemId(null);
  };

  const confirmDelete = async () => {
    if (!deleteItemId || deleting) return;
    setDeleting(true);
    let deleted: boolean | undefined;
    try {
      deleted = await actions.moveToBin(deleteItemId);
    } finally {
      setDeleting(false);
    }
    if (!deleted) return;
    if (selectedItem) deletedItems.current.add(recordKey(selectedItem));
    runDetailsAfterSave(() =>
      updateStates(
        { 'inventory.delete': null, 'inventory.editor': null },
        { replace: true },
      ),
    );
    imageWorkflow.reset();
  };

  const duplicate = async (item: Item) => {
    if (!isItemWritable(item)) return;
    try {
      await requestTransition(
        { 'inventory.editor': { mode: 'add' } },
        () => {
          imageWorkflow.reset();
          setDuplicateDraft(true);
          setDuplicateSourceItemId(
            item.storagePath || item.image ? item.id : undefined,
          );
          setKeywords(`${item.keywords} (Copy)`);
          const location = findItemLocation(item);
          const duplicateLocation = location ? recordKey(location) : '';
          setLocation(duplicateLocation);
          setQty(item.qty);
          setTagIds([]);
          imageWorkflow.setExternalImageUrl(item.image);
          imageWorkflow.setCroppedImageBlob(null);
          setAddBaseline({
            keywords: `${item.keywords} (Copy)`,
            location: duplicateLocation,
            qty: item.qty,
            hasImage: Boolean(item.image || item.storagePath),
          });
        },
        { replace: true },
      );
    } catch (error) {
      logger.error('Error preparing item for duplication:', error);
      notify(
        'Failed to prepare item for duplication. Please try again.',
        'error',
      );
    }
  };

  const resetForCollectionChange = () => {
    setDuplicateDraft(false);
    setDuplicateSourceItemId(undefined);
    setSelectedItem(null);
    setAddBaseline(null);
    setKeywords('');
    setLocation('');
    setQty(1);
    setTagIds([]);
    imageWorkflow.reset();
    runDetailsAfterSave(() =>
      updateStates(
        { 'inventory.editor': null, 'inventory.delete': null },
        { replace: true },
      ),
    );
  };

  return {
    openAdd,
    openAddAtLocation,
    openItem,
    targetCollection,
    setCurrentLocation: setLocation,
    hasUnsavedChanges: addDirty || detailsDirty,
    resetForCollectionChange,
    modalProps: {
      addItem: {
        tagControl,
        tagsPending,
        tagIds:
          canManageTags && editorState.tagIds.length
            ? editorState.tagIds
            : undefined,
        onPendingChange: setAddUiPending,
        onRapidCaptureChange: setAddRapidCapture,
        analysisAvailable: !duplicateDraft && Boolean(enqueuePhoto),
        aiLocationRequired: !chosenLocation && !addCollectionId,
        pendingImage: imageWorkflow.croppedImageBlob,
        aiAuthority: addAiAuthority,
        open: addOpen,
        onClose: () => {
          void requestAddClose();
        },
        keywords: editorState.keywords,
        setKeywords,
        location: editorState.location,
        setLocation,
        qty: editorState.qty,
        setQty,
        handleSave: (analyzeImage?: boolean) => saveAdd(false, analyzeImage),
        handleSaveAndAddAnother: async (analyzeImage?: boolean) => {
          const succeeded = await saveAdd(true, analyzeImage);
          if (succeeded)
            setAddBaseline({
              keywords: '',
              location: editorState.location,
              qty: 1,
              hasImage: false,
            });
          return succeeded;
        },
        locations: destinationLocations,
        setShowAddLocationModal: (open: boolean) =>
          open && onAddLocation(targetCollectionId),
        image: imageWorkflow.image,
        lastUsedLocation: activeLastUsedLocation,
        compressionInProgress: imageWorkflow.compressionInProgress,
        compressionProgress: imageWorkflow.compressionProgress,
        handleImageCapture: imageWorkflow.handleImageCapture,
        handleClearImage: () => {
          setDuplicateSourceItemId(undefined);
          imageWorkflow.clearPendingImage();
        },
        updateLastUsedLocation: applyLastUsedLocation,
        ...editorActions,
      },
      deleteConfirmation: {
        open: deleteOpen,
        onClose: cancelDelete,
        handleConfirmDelete: confirmDelete,
        deleting,
      },
      itemDetails: {
        tagControl,
        tagsPending,
        open: detailsOpen,
        onClose: () => {
          void requestDetailsClose();
        },
        selectedItem,
        image: imageWorkflow.removeExistingImage
          ? null
          : imageWorkflow.image || selectedItem?.image || null,
        keywords: editorState.keywords,
        setKeywords,
        location: editorState.location,
        setLocation,
        qty: editorState.qty,
        setQty,
        handleUpdate: (itemId: string) =>
          runSave(async () => {
            if (tagsPending) return false;
            const destination = crossCollectionLocation;
            const item = selectedItem;
            if (!destination || !item || !onMoveItem)
              return actions.update(itemId);
            // Save content/image edits in the source before the separately recoverable move.
            if (!(await actions.update(itemId))) return false;
            return canManageTags && editorState.tagIds.length
              ? onMoveItem(item, destination, editorState.tagIds)
              : onMoveItem(item, destination);
          }),
        saveLabel: crossCollectionLocation ? 'Save and move' : undefined,
        locationNotice:
          canMove && !crossCollectionLocation ? moveLocationsNotice : undefined,
        locationsLoading: canMove && moveLocationsLoading,
        handleDeleteFromModal: requestDelete,
        locations: destinationLocations,
        setShowAddLocationModal: (open: boolean) =>
          open && onAddLocation(targetCollectionId),
        handleImageCapture: imageWorkflow.handleImageCapture,
        handleClearImage: imageWorkflow.handleClearImage,
        compressionInProgress: imageWorkflow.compressionInProgress,
        compressionProgress: imageWorkflow.compressionProgress,
        handleDuplicate: duplicate,
        updateLastUsedLocation: applyLastUsedLocation,
        currentUserId: collectionContext.userId,
        currentUsername,
        readOnly: !targetCanEdit,
        canDelete: Boolean(targetCanDelete),
        collectionId: targetCollectionId,
        membershipVersion: targetMembershipVersion,
        pendingImage: imageWorkflow.croppedImageBlob,
        aiAuthority:
          targetAuthority && targetMembershipVersion !== undefined
            ? { ...targetAuthority, membershipVersion: targetMembershipVersion }
            : undefined,
        ...editorActions,
      },
    },
  };
}

export type ItemWorkflows = ReturnType<typeof useItemWorkflows>;
